import { faChevronDown } from "@fortawesome/pro-light-svg-icons/faChevronDown";
import { faCirclePlus } from "@fortawesome/pro-light-svg-icons/faCirclePlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import React from "react";

import { cn } from "../lib/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    variant?: "arrow" | "plus";
  }
>(({ className, children, variant = "arrow", ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline",
        variant === "arrow" && "[&[data-state=open]>svg]:rotate-180",
        variant === "plus" && "[&[data-state=open]>svg]:rotate-45",
        className,
      )}
      {...props}
    >
      {children}
      <FontAwesomeIcon
        icon={variant === "plus" ? faCirclePlus : faChevronDown}
        className={cn("h-4 w-4 shrink-0 transition-transform duration-200")}
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    contentWrapperClassName?: string;
  }
>(
  (
    { className, contentWrapperClassName, children, forceMount, ...props },
    ref,
  ) => (
    // With forceMount, closed content stays in the server-rendered HTML (so
    // search and AI crawlers can read it) and Radix no longer hides it for us —
    // the grid-rows transition handles the collapse instead.
    <AccordionPrimitive.Content
      ref={ref}
      forceMount={forceMount}
      className={cn(
        "text-sm",
        forceMount
          ? "grid transition-[grid-template-rows,visibility] duration-200 data-[state=closed]:invisible data-[state=closed]:grid-rows-[0fr] data-[state=open]:grid-rows-[1fr]"
          : "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden transition-all",
        className,
      )}
      {...props}
    >
      <div className={cn(forceMount && "min-h-0 overflow-hidden")}>
        <div className={cn("pb-4 pt-0", contentWrapperClassName)}>
          {children}
        </div>
      </div>
    </AccordionPrimitive.Content>
  ),
);
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
